import { Component } from "@theme/component";
import {
  onDocumentLoaded,
  changeMetaThemeColor,
  setHeaderMenuStyle,
} from "@theme/utilities";

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ["headerDrawerContainer", "headerMenu", "headerRowTop"];

  #isHomepageEditorialMenu() {
    return this.dataset.editorialMenu !== undefined;
  }

  #isDesktopEditorialMenuActive() {
    return (
      this.#isHomepageEditorialMenu() &&
      window.matchMedia("(min-width: 750px)").matches &&
      this.dataset.menuStyle === "menu"
    );
  }

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * Keeps the always-sticky header in its compact layout while the background exits.
   * @type {number | null}
   */
  #stickyExitTimeout = null;

  /**
   * Current FLIP animation for the editorial homepage logo.
   * @type {Animation | null}
   */
  #editorialLogoAnimation = null;

  /**
   * Cleanup for the temporary logo clone used while the sticky header exits.
   * @type {(() => void) | null}
   */
  #editorialLogoExitCleanup = null;

  /**
   * Cleanup for the temporary menu clone used during sticky entry.
   * @type {(() => void) | null}
   */
  #editorialMenuExitCleanup = null;

  /**
   * Floating pill element for the editorial header menu hover effect.
   * @type {HTMLElement | null}
   */
  #editorialMenuPill = null;

  /**
   * Cleanup function for the editorial menu pill event listeners.
   * @type {(() => void) | null}
   */
  #editorialMenuPillCleanup = null;

  /**
   * Timeout used to keep the sticky editorial overlay alive while its panel exits.
   * @type {number | null}
   */
  #editorialStickyMenuCleanupTimeout = null;

  /**
   * Mutation observer used while the sticky drawer routes into the editorial menu.
   * @type {MutationObserver | null}
   */
  #editorialStickyMenuObserver = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty(
      "--header-height",
      `${roundedHeaderHeight}px`,
    );

    // Check if the menu drawer should be hidden in favor of the header menu
    if (
      this.#menuDrawerHiddenWidth &&
      window.innerWidth > this.#menuDrawerHiddenWidth
    ) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Animates the homepage editorial logo between its natural and sticky positions.
   * @param {() => void} updateLayout
   */
  #animateEditorialLogoTransition(updateLayout) {
    const logo = this.querySelector(
      ".header__columns:has(.editorial-header-menu) .header-logo",
    );
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (!(logo instanceof HTMLElement) || reduceMotion) {
      updateLayout();
      return;
    }

    this.#cleanupEditorialLogoExit();
    this.#editorialLogoAnimation?.cancel();
    const before = logo.getBoundingClientRect();

    updateLayout();

    requestAnimationFrame(() => {
      const after = logo.getBoundingClientRect();
      const deltaX = before.left - after.left;
      const deltaY = before.top - after.top;

      if (Math.abs(deltaX) < 0.5 && Math.abs(deltaY) < 0.5) return;

      const finalTransform = getComputedStyle(logo).transform;
      const baseTransform = finalTransform === "none" ? "" : finalTransform;

      this.#editorialLogoAnimation = logo.animate(
        [
          { transform: `translate(${deltaX}px, ${deltaY}px) ${baseTransform}` },
          { transform: baseTransform || "none" },
        ],
        {
          duration: 440,
          easing: "cubic-bezier(0.16, 1, 0.3, 1)",
          fill: "none",
        },
      );
    });
  }

  #cleanupEditorialLogoExit() {
    this.#editorialLogoExitCleanup?.();
    this.#editorialLogoExitCleanup = null;
  }

  #cleanupEditorialMenuExit() {
    this.#editorialMenuExitCleanup?.();
    this.#editorialMenuExitCleanup = null;
  }

  #lockEditorialUnderlayExitHeight() {
    const underlay = this.querySelector(".header__underlay-closed");
    if (!(underlay instanceof HTMLElement)) return;

    this.style.setProperty(
      "--editorial-exit-underlay-height",
      `${underlay.getBoundingClientRect().height}px`,
    );
  }

  #clearEditorialUnderlayExitHeight() {
    this.style.removeProperty("--editorial-exit-underlay-height");
  }

  #clearEditorialStickyMenuState() {
    if (this.#editorialStickyMenuCleanupTimeout) {
      clearTimeout(this.#editorialStickyMenuCleanupTimeout);
      this.#editorialStickyMenuCleanupTimeout = null;
    }

    this.#editorialStickyMenuObserver?.disconnect();
    this.#editorialStickyMenuObserver = null;

    delete this.dataset.editorialStickyMenuOpen;
    delete this.dataset.editorialStickyMenuOverlay;

    const editorialMenu = this.querySelector("editorial-header-menu");
    const primary = this.querySelector(".editorial-header-menu__primary");

    if (
      editorialMenu instanceof HTMLElement &&
      "close" in editorialMenu &&
      typeof editorialMenu.close === "function"
    ) {
      editorialMenu.close();
    }

    if (editorialMenu instanceof HTMLElement) {
      editorialMenu.style.removeProperty("--editorial-menu-nav-width");
      editorialMenu.style.removeProperty("--editorial-menu-panel-top-pad");
      editorialMenu.style.removeProperty("--editorial-menu-primary-left");
      editorialMenu.style.removeProperty("--editorial-menu-primary-top");
    }

    if (primary instanceof HTMLElement) {
      primary.style.position = "";
      primary.style.left = "";
      primary.style.top = "";
      primary.style.width = "";
      primary.style.transition = "";
      primary.style.opacity = "";
    }
  }

  /**
   * Copies a small, explicit set of computed styles so animation clones keep
   * the same typography and visual treatment as the live editorial menu.
   */
  /**
   * @param {CSSStyleDeclaration} computed
   * @param {HTMLElement} target
   * @param {string[]} properties
   */
  #copyComputedStyles(computed, target, properties) {
    properties.forEach((property) => {
      target.style.setProperty(property, computed.getPropertyValue(property));
    });
  }

  /**
   * @param {HTMLElement} clone
   * @param {HTMLElement} original
   */
  #freezeCloneStyles(clone, original) {
    const originalComputed = getComputedStyle(original);
    this.#copyComputedStyles(originalComputed, clone, [
      "font-family",
      "font-style",
      "font-weight",
      "font-size",
      "line-height",
      "letter-spacing",
      "text-transform",
      "color",
      "text-shadow",
      "gap",
      "justify-items",
    ]);

    const sources = original.querySelectorAll(
      ".editorial-header-menu__trigger",
    );
    const targets = clone.querySelectorAll(".editorial-header-menu__trigger");
    targets.forEach((target, i) => {
      if (!(target instanceof HTMLElement)) return;
      const source = sources[i];
      if (!(source instanceof HTMLElement)) return;
      const cs = getComputedStyle(source);
      this.#copyComputedStyles(cs, target, [
        "font-family",
        "font-style",
        "font-weight",
        "font-size",
        "line-height",
        "letter-spacing",
        "text-transform",
        "color",
        "text-shadow",
        "opacity",
      ]);
    });
  }

  #animateEditorialMenuExit() {
    if (!this.#isDesktopEditorialMenuActive()) return;

    this.#cleanupEditorialMenuExit();

    const menuPrimary = this.querySelector(".editorial-header-menu__primary");
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (!(menuPrimary instanceof HTMLElement) || reduceMotion) return;

    const rect = menuPrimary.getBoundingClientRect();
    const clone = /** @type {HTMLElement} */ (menuPrimary.cloneNode(true));
    clone.setAttribute("aria-hidden", "true");
    this.#freezeCloneStyles(clone, menuPrimary);
    Object.assign(clone.style, {
      position: "fixed",
      left: `${rect.left}px`,
      top: `${rect.top}px`,
      width: `${rect.width}px`,
      margin: "0",
      pointerEvents: "none",
      zIndex: "calc(var(--layer-overlay) + 2)",
    });

    this.appendChild(clone);

    clone.animate(
      [
        { opacity: "1", transform: "translateX(0)" },
        { opacity: "0", transform: "translateX(-4rem)" },
      ],
      {
        duration: 380,
        easing: "cubic-bezier(0.16, 1, 0.3, 1)",
        fill: "forwards",
      },
    );

    this.#editorialMenuExitCleanup = () => {
      clone.remove();
    };
  }

  /**
   * Animates the live editorial header menu back into place without changing
   * the sticky header's compact layout while the background is exiting.
   */
  #animateEditorialMenuEnter() {
    if (!this.#isDesktopEditorialMenuActive()) return;

    this.#cleanupEditorialMenuExit();

    const menuPrimary = this.querySelector(".editorial-header-menu__primary");
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (!(menuPrimary instanceof HTMLElement) || reduceMotion) return;

    const originalPointerEvents = menuPrimary.style.pointerEvents;
    const originalWillChange = menuPrimary.style.willChange;
    let animation = null;
    let cancelled = false;

    this.dataset.editorialMenuEntering = "true";
    menuPrimary.style.pointerEvents = "none";
    menuPrimary.style.willChange = "transform, opacity";

    animation = menuPrimary.animate(
      [
        { opacity: "0", transform: "translateX(-4rem)" },
        { opacity: "1", transform: "translateX(0)" },
      ],
      {
        duration: 440,
        easing: "cubic-bezier(0.33, 1, 0.68, 1)",
        fill: "both",
      },
    );

    animation.finished
      // `Animation.finished` rejects when the animation is cancelled during
      // a sticky-state interruption; swallow that expected rejection.
      .catch(() => {})
      .finally(() => {
        if (cancelled) return;
        menuPrimary.style.pointerEvents = originalPointerEvents;
        menuPrimary.style.willChange = originalWillChange;
        delete this.dataset.editorialMenuEntering;
        if (this.#editorialMenuExitCleanup) {
          this.#editorialMenuExitCleanup = null;
        }
      });

    this.#editorialMenuExitCleanup = () => {
      cancelled = true;
      animation?.cancel();
      menuPrimary.style.pointerEvents = originalPointerEvents;
      menuPrimary.style.willChange = originalWillChange;
      delete this.dataset.editorialMenuEntering;
    };
  }

  /**
   * Moves the live editorial logo back home with the same FLIP path used on entry.
   */
  #animateEditorialLogoExit(updateLayout = () => {}) {
    this.#cleanupEditorialLogoExit();
    this.#animateEditorialLogoTransition(updateLayout);
  }

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        if (isIntersecting) {
          this.#clearEditorialStickyMenuState();
          if (this.dataset.stickyState === "active") {
            if (!this.#stickyExitTimeout) {
              this.#lockEditorialUnderlayExitHeight();
              this.dataset.stickyExiting = "true";
              this.#animateEditorialLogoExit(() => {
                this.dataset.stickyState = "inactive";
              });
              this.#animateEditorialMenuEnter();
              this.#stickyExitTimeout = window.setTimeout(() => {
                delete this.dataset.stickyExiting;
                delete this.dataset.editorialMenuMeasuring;
                delete this.dataset.editorialMenuEntering;
                this.#cleanupEditorialMenuExit();
                this.#clearEditorialUnderlayExitHeight();
                this.#stickyExitTimeout = null;
              }, 440);
            }
          } else {
            this.dataset.stickyState = "inactive";
            delete this.dataset.stickyExiting;
            delete this.dataset.editorialMenuMeasuring;
            delete this.dataset.editorialMenuEntering;
            this.#clearEditorialUnderlayExitHeight();
          }
        } else {
          if (this.#stickyExitTimeout) {
            clearTimeout(this.#stickyExitTimeout);
            this.#stickyExitTimeout = null;
            delete this.dataset.stickyExiting;
            delete this.dataset.editorialMenuMeasuring;
            delete this.dataset.editorialMenuEntering;
            this.#clearEditorialUnderlayExitHeight();
          }
          this.#cleanupEditorialLogoExit();
          this.#cleanupEditorialMenuExit();
          this.#animateEditorialMenuExit();
          this.#animateEditorialLogoTransition(() => {
            this.dataset.stickyState = "active";
            delete this.dataset.stickyExiting;
          });
        }

        if (this.dataset.themeColor)
          changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen =
          !isIntersecting || this.dataset.stickyState === "active";
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Injects a floating pill element that slides between editorial menu buttons on hover.
   */
  #initEditorialMenuPill() {
    const primary = this.querySelector(".editorial-header-menu__primary");
    if (!primary) return;

    const pill = document.createElement("span");
    pill.className = "editorial-header-menu__pill";
    pill.setAttribute("aria-hidden", "true");
    primary.appendChild(pill);
    this.#editorialMenuPill = pill;

    const triggers = Array.from(
      primary.querySelectorAll(".editorial-header-menu__trigger"),
    );

    /**
     * @param {HTMLElement} trigger
     */
    const moveToTrigger = (trigger) => {
      const primaryRect = primary.getBoundingClientRect();
      const triggerRect = trigger.getBoundingClientRect();
      const remSize = parseFloat(
        getComputedStyle(document.documentElement).fontSize,
      );
      const pad = 0.18 * remSize;

      if (pill.style.opacity === "0") {
        pill.style.transition = "none";
        pill.style.transform = `translateX(${triggerRect.left - primaryRect.left}px)`;
        pill.style.top = `${triggerRect.top - primaryRect.top - pad}px`;
        pill.style.width = `${triggerRect.width}px`;
        pill.style.height = `${triggerRect.height + pad * 2}px`;

        requestAnimationFrame(() => {
          pill.style.transition = "";
          pill.style.opacity = "1";
        });
        return;
      }

      pill.style.opacity = "1";
      pill.style.top = `${triggerRect.top - primaryRect.top - pad}px`;
      pill.style.width = `${triggerRect.width}px`;
      pill.style.height = `${triggerRect.height + pad * 2}px`;
      pill.style.transform = `translateX(${triggerRect.left - primaryRect.left}px)`;
    };

    const hidePill = () => {
      pill.style.opacity = "0";
    };

    triggers.forEach((trigger) => {
      if (!(trigger instanceof HTMLElement)) return;

      const onEnter = () => {
        // If a panel is already open and this trigger is the active one, keep ::after visible
        if (trigger.getAttribute("aria-expanded") === "true") return;
        moveToTrigger(trigger);
      };
      const onLeave = () => {
        hidePill();
      };

      trigger.addEventListener("pointerenter", onEnter);
      trigger.addEventListener("pointerleave", onLeave);
    });

    this.#editorialMenuPillCleanup = () => {
      pill.remove();
      this.#editorialMenuPill = null;
    };
  }

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute("sticky");
    if (!this.#offscreen && stickyMode !== "always") return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === "always") {
      if (isAtTop) {
        this.dataset.scrollDirection = "none";
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = "up";
      } else {
        this.dataset.scrollDirection = "down";
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = "inactive";
        this.dataset.scrollDirection = "none";
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = "active";
        this.dataset.scrollDirection = "up";
      }
    } else if (this.dataset.stickyState === "active") {
      this.dataset.scrollDirection = "none";

      this.dataset.stickyState = "idle";
    } else {
      this.dataset.scrollDirection = "none";
      this.dataset.stickyState = "idle";
    }

    this.#lastScrollTop = scrollTop;
  };

  /**
   * Routes the sticky desktop drawer button into the editorial side panel instead
   * of the standard drawer, while keeping the panel in its measured stacked layout.
   * @param {MouseEvent} event
   */
  #handleDrawerClick = (event) => {
    if (!window.matchMedia("(min-width: 750px)").matches) return;
    if (this.dataset.stickyState !== "active") return;

    const editorialMenu = this.querySelector("editorial-header-menu");
    const firstTrigger = editorialMenu?.querySelector(
      "[data-editorial-menu-trigger]",
    );
    const headerMenu = firstTrigger?.closest("header-menu");
    const primary = editorialMenu?.querySelector(
      ".editorial-header-menu__primary",
    );

    if (
      !(editorialMenu instanceof HTMLElement) ||
      !(firstTrigger instanceof HTMLElement) ||
      !(headerMenu instanceof HTMLElement)
    )
      return;

    const editorialMenuElement =
      /** @type {HTMLElement & { open: (key?: string, trigger?: HTMLElement) => void }} */ (
        editorialMenu
      );

    event.preventDefault();
    event.stopPropagation();
    this.#clearEditorialStickyMenuState();

    if (primary instanceof HTMLElement) {
      primary.style.transition = "opacity 0.34s ease";
      primary.style.opacity = "0";
    }

    this.#animateEditorialLogoTransition(() => {
      this.dataset.editorialStickyMenuOpen = "true";
      this.dataset.editorialStickyMenuOverlay = "true";
    });

    editorialMenuElement.open(
      firstTrigger.dataset.editorialMenuTrigger,
      firstTrigger,
    );

    requestAnimationFrame(() => {
      if (primary instanceof HTMLElement) {
        primary.style.opacity = "1";
      }
    });

    const cleanup = () => {
      this.#editorialStickyMenuObserver?.disconnect();
      this.#editorialStickyMenuObserver = null;

      this.#animateEditorialLogoTransition(() => {
        delete this.dataset.editorialStickyMenuOpen;
      });

      if (primary instanceof HTMLElement) {
        primary.style.transition = "opacity 0.24s ease";
        primary.style.opacity = "0";
      }

      this.#editorialStickyMenuCleanupTimeout = window.setTimeout(() => {
        delete this.dataset.editorialStickyMenuOverlay;
        this.#editorialStickyMenuCleanupTimeout = null;
        if (primary instanceof HTMLElement) {
          primary.style.transition = "";
          primary.style.opacity = "";
        }
      }, 360);
    };

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.attributeName === "data-open" &&
          !editorialMenuElement.dataset.open
        ) {
          observer.disconnect();
          cleanup();
        }
      }
    });
    this.#editorialStickyMenuObserver = observer;
    observer.observe(editorialMenuElement, { attributes: true });
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener("overflowMinimum", this.#handleOverflowMinimum);
    this.#initEditorialMenuPill();

    const drawerSummary = this.querySelector(
      '[ref="headerDrawerContainer"] details summary',
    );
    drawerSummary?.addEventListener("click", this.#handleDrawerClick, {
      capture: true,
    });

    const stickyMode = this.getAttribute("sticky");
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === "always");

      if (stickyMode === "scroll-up" || stickyMode === "always") {
        document.addEventListener("scroll", this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener("overflowMinimum", this.#handleOverflowMinimum);

    const drawerSummary = this.querySelector(
      '[ref="headerDrawerContainer"] details summary',
    );
    drawerSummary?.removeEventListener("click", this.#handleDrawerClick, {
      capture: true,
    });

    document.removeEventListener("scroll", this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    if (this.#stickyExitTimeout) {
      clearTimeout(this.#stickyExitTimeout);
      this.#stickyExitTimeout = null;
    }
    this.#clearEditorialStickyMenuState();
    this.#editorialLogoAnimation?.cancel();
    this.#editorialLogoAnimation = null;
    this.#cleanupEditorialLogoExit();
    this.#cleanupEditorialMenuExit();
    this.#editorialMenuPillCleanup?.();
    this.#editorialMenuPillCleanup = null;
    document.body.style.setProperty("--header-height", "0px");
  }
}

if (!customElements.get("header-component")) {
  customElements.define("header-component", HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector("header-component");
  const headerGroup = document.querySelector("#header-group");

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const updateHeaderGroupHeight = () => {
      let headerGroupHeight = 0;
      const children = headerGroup.children;

      for (let i = 0; i < children.length; i++) {
        const element = children[i];
        if (!(element instanceof HTMLElement)) continue;
        headerGroupHeight += element.offsetHeight;
      }

      if (
        header instanceof HTMLElement &&
        header.hasAttribute("transparent") &&
        header.parentElement?.nextElementSibling
      ) {
        headerGroupHeight += header.offsetHeight;
      }

      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty(
        "--header-group-height",
        `${roundedHeaderGroupHeight}px`,
      );
    };

    const resizeObserver = new ResizeObserver(() => {
      updateHeaderGroupHeight();
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }

          updateHeaderGroupHeight();
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
